<script setup>
import EnTextField from 'Components/inputs/EnTextFieldV2/EnTextFieldV2.vue';
import EnWidgetCard from 'Components/widgets/EnWidgetCard/EnWidgetCard';
import EnOverlaySpinner from 'Components/feedback/EnOverlaySpinner/EnOverlaySpinner';
import EnValidationErrorMessage from 'Components/feedback/EnValidationErrorMessage/EnValidationErrorMessage.vue';
import EnButton from 'Components/base/EnButton/EnButton.vue';

import { getUrlParams } from 'Js/utils/urlUtils';
import { required, validEmail, minLength, maxLength } from 'Js/validation/rules';
import { useSessionStorage } from 'Components/navigation/utils/useSessionStorage';
import { EnButtonVariants } from 'Components/types/EnButtonVariants';


const code = ref('');
const email = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const loading = ref(false);
const errors = ref([]);
const successRequest = ref(null);
const form = ref();
const showPassword = ref(false);
const {getIsEnTab} = useSessionStorage();
const isEnTab = computed(() => {
  return getIsEnTab();
});

const emailRules = ref([
  required,
  validEmail
]);
const passwordRules = ref([
  required,
  val => minLength(val, 6),
  val => maxLength(val, 100),
  val => /[0-9]/.test(val) || t('Authentication.manage.password.passwordRequiresDigit'),
  val => /[a-z]/.test(val) || t('Authentication.manage.password.passwordRequiresLower'),
  val => /[A-Z]/.test(val) || t('Authentication.manage.password.passwordRequiresUpper'),
  val => /[^A-Za-z0-9]/.test(val) || t('Authentication.manage.password.passwordRequiresNonAlphanumeric'),
]);
const confirmPasswordRules = ref([
  required, val => minLength(val, 6), val => maxLength(val, 100), val => val === newPassword.value || t('Authentication.password.reset.passwordsDoNotMatch')
]);
const isInvalid = computed(() => {
  return !(form.value && form.value.isValid);
});
const isInvalidToken = ref(false);
const backToLoginUrl = computed(() => {
  return `/Account/Login${isEnTab.value ? 'EnTab' : ''}`;
});

function togglePasswordVisibility() {
  showPassword.value = !showPassword.value;
}

async function sendRequest() {
  loading.value = true;
  errors.value = [];
  isInvalidToken.value = false;

  const response = await fetch(`/api/Manage/ResetPassword`, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({Email: email.value, NewPassword: newPassword.value, confirmPassword: confirmPassword.value, code: code.value})
  });

  if (response.ok) {
    try {
      const result = await response.json();
      if (result.invalidToken === true) {
        errors.value.push(t('Authentication.password.reset.invalidToken'));
        isInvalidToken.value = true;
      } else if (result.errors) {
        errors.value.push(t('Authentication.password.forgot.unexpectedException'));
      } else {
        errors.value = [];
        successRequest.value = true;
      }
    } catch {
      errors.value = [];
      successRequest.value = true;
    }

  } else {
    errors.value.push(t('Authentication.password.forgot.unexpectedException'));
  }
  loading.value = false;
}

onBeforeMount(async () => {
  const params = getUrlParams(window.location);

  code.value = params['code'];
});
onMounted(() => {
  form.value.validate();
});
</script>

<template>
  <div class="main-overview restricted-width">
    <en-overlay-spinner v-model="loading"></en-overlay-spinner>

    <en-widget-card
        icon-class="icon-enloc_logo_min"
        :title="t('Authentication.password.reset.title')"
        :max-width="450"
        :padding="32"
    >

      <div class="form-group-bordered" v-if="successRequest !== true">
        <h5>{{ t('Authentication.password.reset.subtitle') }}</h5>

        <div class="mt-5 mb-1" v-if="errors?.length">
          <div v-for="(error, i) in errors" :key="i">
            <div>
              <en-validation-error-message :error="error"></en-validation-error-message>
              <a v-if="isInvalidToken" href="/Account/Profile/ForgotPassword">link</a>
            </div>
          </div>
        </div>

        <v-form ref="form" class="w-100 form-wrapper">
          <div class="form-group">
            <div class="text-field-container">
              <div class="label">{{ t('Authentication.password.reset.email') }}</div>
              <en-text-field
                  variant="outlined"
                  rounded="lg"
                  type="email"
                  density="compact"
                  v-model="email"
                  :rules="emailRules"
              ></en-text-field>
            </div>

            <div class="text-field-container">
              <div class="label">{{ t('Authentication.password.reset.newPassword') }}</div>
              <en-text-field
                  variant="outlined"
                  rounded="lg"
                  density="compact"
                  v-model="newPassword"
                  :rules="passwordRules"
                  :type="showPassword ? 'text' : 'password'"
                  :suffix-icon-class="newPassword ? (showPassword ? 'mdi-eye-off' : 'mdi-eye') : null"
                  :on-suffix-icon-click="togglePasswordVisibility"
              ></en-text-field>
            </div>

            <div class="text-field-container">
              <div class="label">{{ t('Authentication.password.reset.confirmPassword') }}</div>
              <en-text-field
                  variant="outlined"
                  rounded="lg"
                  density="compact"
                  v-model="confirmPassword"
                  :rules="confirmPasswordRules"
                  :type="showPassword ? 'text' : 'password'"
                  :suffix-icon-class="confirmPassword ? (showPassword ? 'mdi-eye-off' : 'mdi-eye') : null"
                  :on-suffix-icon-click="togglePasswordVisibility"
              ></en-text-field>
            </div>
          </div>

          <div class="login-block mt-6">
            <en-button
                :static-id="reset-password-button"
                :variant="EnButtonVariants.FILLED"
                :no-shadow="true"
                class="button-text"
                width="100%"
                @click="sendRequest"
                :disabled="isInvalid"
            >
              {{ t('Authentication.password.reset.buttonText') }}
            </en-button>
          </div>
        </v-form>
      </div>

      <div v-else class="form-group-bordered w-100">
        <h5>{{ t('Authentication.password.reset.successMessage') }}</h5>
        <div class="login-block mt-6">
          <a :href="backToLoginUrl" class="w-100">
            <en-button
                :static-id="back-to-login-button"
                :no-shadow="true"
                class="button-text"
                width="100%"
            >
              {{ t('Authentication.password.backToLogin') }}
            </en-button>
          </a>
        </div>
      </div>
    </en-widget-card>
  </div>
</template>

<style scoped lang="scss">
@use "Styles/helper/variables.scss";

.main-overview {
  min-height: 80vh;
  display: flex;
  justify-content: center;
  align-items: center;
}

.form-group-bordered {
  margin-top: 15px;
  width: 100%;
}

.button-text {
  border: 1px solid variables.$login-page-btn-text;
  text-transform: none;
}

.label {
  display: block;
  font-size: 0.975rem;
  color: variables.$en-blue !important;
  font-weight: 300;
}

.login-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
  text-align: left;
}

.form-wrapper {
  margin-top: 40px;
}
</style>
